import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContentContext = createContext({ content: {}, products: [], catalog: [], reload: () => {} });

export const ContentProvider = ({ children }) => {
  const [content, setContent] = useState({});
  const [products, setProducts] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const lastFetchRef = useRef(0);

  const reload = useCallback(async () => {
    // Cache-bust to defeat any intermediate proxy or browser cache.
    const ts = Date.now();
    lastFetchRef.current = ts;
    try {
      const [c, p, cat] = await Promise.all([
        axios.get(`${API}/content?_=${ts}`),
        axios.get(`${API}/products?_=${ts}`),
        axios.get(`${API}/catalog?_=${ts}`),
      ]);
      setContent(c.data || {});
      setProducts(p.data || []);
      setCatalog(cat.data || []);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Content fetch failed; using defaults', err.message);
    } finally {
      setLoaded(true);
    }
  }, []);

  // Initial load.
  useEffect(() => { reload(); }, [reload]);

  // Refetch whenever the tab regains focus or the page is re-shown
  // (handles "I edited in admin, switched back to the public site" without
  // requiring a hard refresh).
  useEffect(() => {
    const STALE_MS = 15 * 1000;
    const maybeRefresh = () => {
      if (Date.now() - lastFetchRef.current > STALE_MS) reload();
    };
    const onVisibility = () => { if (!document.hidden) maybeRefresh(); };
    window.addEventListener('focus', maybeRefresh);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', maybeRefresh);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [reload]);

  return (
    <ContentContext.Provider value={{ content, products, catalog, loaded, reload }}>
      {children}
    </ContentContext.Provider>
  );
};

export const useContent = () => useContext(ContentContext);

// Helper to safely read a section with code-level fallback
export const useSection = (key, fallback = {}) => {
  const { content } = useContent();
  return { ...fallback, ...(content[key] || {}) };
};

// Helper to resolve uploaded image paths to absolute URLs
export const resolveAsset = (image) => {
  if (!image) return '';
  if (image.startsWith('http://') || image.startsWith('https://')) return image;
  if (image.startsWith('/api/')) return `${process.env.REACT_APP_BACKEND_URL}${image}`;
  return image;
};
